import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './BracketConnector.module.scss';

const BracketConnector = ({
  topMatch,
  bottomMatch,
  resultMatch,
  isStraight,
  color,
}) => {
  const paths = useMemo(
    () => {
      const svgLeft = topMatch.x;
      const svgTop = topMatch.y;
      const svgWidth = resultMatch.x - topMatch.x;
      const svgHeight = bottomMatch.y + bottomMatch.height - topMatch.y;

      if (svgWidth <= 0 || svgHeight <= 0) return null;

      const y1 = topMatch.y + topMatch.height / 2 - svgTop;
      const y2 = bottomMatch.y + bottomMatch.height / 2 - svgTop;
      const ymid = (y1 + y2) / 2;

      const x0 = 0;
      const x1 = svgWidth;

      let dTop, dBottom;

      if (isStraight) {
        const xMid = svgWidth / 2;
        dTop = `M ${x0} ${y1} H ${xMid} V ${ymid} H ${x1}`;
        dBottom = `M ${x0} ${y2} H ${xMid} V ${ymid}`;
      } else {
        const ENTRY = 10;
        const MAX_R = 24;
        const R = Math.max(
          4,
          Math.min(MAX_R, Math.abs(y1 - ymid), svgWidth - ENTRY - 4),
        );
        const mergeX = ENTRY + R;

        dTop = [
          `M ${x0} ${y1}`,
          `L ${ENTRY} ${y1}`,
          `A ${R} ${R} 0 0 1 ${mergeX} ${y1 + R}`, // arc clockwise
          `L ${mergeX} ${ymid}`,
          `L ${x1} ${ymid}`,
        ].join(' ');

        dBottom = [
          `M ${x0} ${y2}`,
          `L ${ENTRY} ${y2}`,
          `A ${R} ${R} 0 0 0 ${mergeX} ${y2 - R}`, // arc counter-clockwise
          `L ${mergeX} ${ymid}`,
          `L ${x1} ${ymid}`,
        ].join(' ');
      }

      return { dTop, dBottom, svgLeft, svgTop, svgWidth, svgHeight };
    },
    [topMatch, bottomMatch, resultMatch, isStraight],
  );

  if (!paths) return null;

  const { dTop, dBottom, svgLeft, svgTop, svgWidth, svgHeight } = paths;

  return (
    <svg
      className={styles['bracket-connector']}
      style={{
        top: svgTop,
        left: svgLeft,
        width: svgWidth,
        height: svgHeight,
      }}
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d={dTop}
        stroke={color}
        strokeWidth="1"
        fill="none"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={dBottom}
        stroke={color}
        strokeWidth="1"
        fill="none"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const matchShape = PropTypes.shape({
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
});

BracketConnector.propTypes = {
  topMatch: matchShape.isRequired,
  bottomMatch: matchShape.isRequired,
  resultMatch: matchShape.isRequired,
  isStraight: PropTypes.bool,
  color: PropTypes.string,
};

BracketConnector.defaultProps = {
  isStraight: false,
  color: 'var(--bracket-connector-color)',
};

export default BracketConnector;
